import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Toster from "../../utils/Toaster";
import { handleAdminUpdatePassword } from "../../services/APIClient";
import { STATUS_CODES } from "../../utils/statusCode";
import { useLoader } from "../../contexts/LoaderContext";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { removeUserData } from "../../store/slices/userSlice";

const ChangePasswordModal = ({ isOpen, setChangePasswordModalOpen }) => {
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  // ✅ Real-time validation effect
  useEffect(() => {
    if (confirmPassword && newPassword !== confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Confirm password must match new password.",
      });
    } else {
      clearErrors("confirmPassword");
    }
  }, [newPassword, confirmPassword, setError, clearErrors]);

  const onSubmit = async (formdata) => {
    const postData = new FormData();
    postData.append("oldPassword", formdata.oldPassword);
    postData.append("newPassword", formdata.newPassword);
    postData.append("confirmPassword", formdata.confirmPassword);

    try {
      setLoading(true);
      const response = await handleAdminUpdatePassword(postData);

      if (response?.code === STATUS_CODES.SUCCESS) {
        Toster("Password changed successfully", "success");
        setChangePasswordModalOpen(false);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      }
    } catch {
      Toster("An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setChangePasswordModalOpen(false);

  const handleTrimOnChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, "");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-full max-w-[550px] m-10 p-6 rounded-lg shadow-lg">
        <h2 className="text-xl text-center font-semibold text-gray-800 mb-4">
          Change Password
        </h2>

        {/* Old Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Old Password
          </label>
          <input
            type="password"
            name="oldPassword"
            placeholder="Enter your old password"
            {...register("oldPassword", {
              required: "Old password is required",
              onChange: handleTrimOnChange,
            })}
            className="w-full px-4 text-black py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.oldPassword && (
            <p className="text-red-500 text-sm mt-1">
              {errors.oldPassword.message}
            </p>
          )}
        </div>

        {/* New Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Password
          </label>
          <input
            type="password"
            name="newPassword"
            placeholder="Enter a new password"
            {...register("newPassword", {
              required: "New password is required.",
              pattern: {
                value:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[#$%^&*@~!]).{8,20}$/,
                message:
                  "Password must have uppercase, lowercase, number & special char.",
              },
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters.",
              },
              maxLength: {
                value: 20,
                message: "Password cannot exceed 20 characters.",
              },
              onChange: handleTrimOnChange,
            })}
            className="w-full px-4 text-black py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.newPassword && (
            <p className="text-red-500 text-sm mt-1">
              {errors.newPassword.message}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm your new password"
            {...register("confirmPassword", {
              required: "Confirm password is required.",
              validate: (value) =>
                value === watch("newPassword") ||
                "Confirm password must match new password.",
              onChange: handleTrimOnChange,
            })}
            className="w-full px-4 text-black py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            className="px-4 py-2 bg-gray-700 text-white font-medium rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
